import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/context/AuthContext";
import { Check, ArrowRight, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  useEffect(() => {
    const provider = searchParams.get("provider");
    // Razorpay: /razorpay/verify already ran client-side, so just refresh auth.
    if (provider === "razorpay") {
      checkAuth();
      setStatus("success");
      return;
    }
    const sessionId = searchParams.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    pollStatus(sessionId);
  }, []);

  const pollStatus = async (sessionId) => {
    const maxAttempts = 6;
    const poll = async (attempt) => {
      if (attempt >= maxAttempts) { setStatus("timeout"); return; }
      try {
        const { data } = await API.get(`/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          checkAuth(); // Refresh user data with new plan
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setStatus("error");
      }
    };
    poll(0);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--surface-cream)" }}>
      <div className="buddy-card p-10 text-center max-w-md w-full animate-fadeIn">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center animate-spin" style={{ border: "3px solid var(--surface-border)", borderTopColor: "var(--rolex-green)" }} />
            <h2 className="font-display text-2xl font-semibold" style={{ color: "var(--text-primary)" }}>Processing Payment...</h2>
            <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>Please wait while we confirm your payment</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "var(--success-bg)" }}>
              <Check size={32} style={{ color: "var(--success)" }} />
            </div>
            <h2 className="font-display text-2xl font-semibold" style={{ color: "var(--text-primary)" }}>Welcome to Your Upgrade!</h2>
            <p className="text-sm mt-2 mb-6" style={{ color: "var(--text-muted)" }}>Your plan has been upgraded. Enjoy all your new features!</p>
            <button onClick={() => navigate("/app")} className="btn-primary py-3 px-8 inline-flex items-center gap-2" data-testid="success-go-dashboard">
              Go to Dashboard <ArrowRight size={16} />
            </button>
          </>
        )}
        {(status === "error" || status === "timeout" || status === "expired") && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "var(--error-bg)" }}>
              <span className="text-2xl" style={{ color: "var(--error)" }}>!</span>
            </div>
            <h2 className="font-display text-2xl font-semibold" style={{ color: "var(--text-primary)" }}>
              {status === "timeout" ? "Payment Pending" : "Payment Issue"}
            </h2>
            <p className="text-sm mt-2 mb-6" style={{ color: "var(--text-muted)" }}>
              {status === "timeout" ? "Your payment may still be processing. Check your email for confirmation." : "Something went wrong. Please try again or contact support."}
            </p>
            <button onClick={() => navigate("/pricing")} className="btn-secondary py-3 px-8" data-testid="back-to-pricing">Back to Pricing</button>
          </>
        )}
      </div>
    </div>
  );
}
